import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDocumentTitle } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';
import { PlusCircleIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';
import { useAuthHttp } from '@hooks';
import {
  MASTER_DATA_IMPORT_CREATE,
  SUPPLIER_BULK_CREATE,
  SUPPLIER_GET,
} from '@constants';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Button } from '../../tailwindComponents/Button';
import { MasterImportModal } from '../../tailwindComponents/MasterImportModal';
import { showAiFeaturesAtom } from '../../store/flags';
import { PendingImportsBanner } from '../MasterDataImportReview/PendingImportsBanner';
import { SkuMenuOption } from '../SKUMaster/components/SkuActionsMenu';
import { Supplier, sourceTypeLabelOf } from './components/supplierTypes';
import { ApiSupplier, apiSupplierToViewModel } from './components/supplierApi';
import {
  SupplierFacetState,
  applyFacets,
  deriveBrandOptions,
  deriveCoverageOptions,
  deriveSourceTypeOptions,
  emptySupplierFacetState,
  hasActiveFacets,
  setSingleSourceType,
  toggleBrand,
  toggleCoverage,
  toggleSourceType,
} from './components/supplierFacets';
import { SupplierFacetRail } from './components/SupplierFacetRail';
import { SupplierToolbar } from './components/SupplierToolbar';
import { SupplierActiveFilters } from './components/SupplierActiveFilters';
import { SupplierTable, SupplierSortColumn } from './components/SupplierTable';

const pages = [
  { name: 'Home', href: '/', current: false },
  { name: 'Suppliers', href: '/supplier-master', current: true },
];

const CERT_RANK: Record<string, number> = {
  valid: 0,
  expiring: 1,
  expired: 2,
  none: 3,
};

const readinessRank = (supplier: Supplier): number => {
  if (!supplier.readiness) return Number.POSITIVE_INFINITY;
  return supplier.readiness.ready ? -1 : supplier.readiness.gaps;
};

const comparatorFor =
  (column: SupplierSortColumn) => (a: Supplier, b: Supplier) => {
    switch (column) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'supplierId':
        return a.supplierId.localeCompare(b.supplierId);
      case 'sourceType':
        return sourceTypeLabelOf(a.sourceType).localeCompare(
          sourceTypeLabelOf(b.sourceType)
        );
      case 'certification':
        return CERT_RANK[a.certStatus] - CERT_RANK[b.certStatus];
      case 'readiness':
        return readinessRank(a) - readinessRank(b);
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  };

export function SupplierMaster() {
  useDocumentTitle('FLOX - Suppliers');
  const navigate = useNavigate();
  const authHttp = useAuthHttp();

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [facets, setFacets] = useState<SupplierFacetState>(
    emptySupplierFacetState
  );
  const [includeArchived, setIncludeArchived] = useState(false);
  const [isRailVisible, setIsRailVisible] = useState(true);
  const [orderBy, setOrderBy] = useState<SupplierSortColumn | null>(null);
  const [isOrderAscending, setIsOrderAscending] = useState(true);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const showAiFeatures = useAtomValue(showAiFeaturesAtom);

  useEffect(() => {
    let isActive = true;
    const fetchSuppliers = async () => {
      setIsLoading(true);
      try {
        const { data } = await authHttp.post(SUPPLIER_GET, {
          pagination: { itemsPerPage: 500 },
        });
        if (!isActive) return;
        const mapped = (data.suppliers as ApiSupplier[]).map(
          apiSupplierToViewModel
        );
        setSuppliers(mapped);
        setTotalCount(data.totalCount ?? mapped.length);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };
    fetchSuppliers();
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCounter]);

  const facetSource = useMemo(() => {
    const base = includeArchived
      ? suppliers
      : suppliers.filter((supplier) => !supplier.isArchived);
    const query = searchQuery.trim().toLowerCase();
    if (!query) return base;
    return base.filter(
      (supplier) =>
        supplier.name.toLowerCase().includes(query) ||
        supplier.supplierId.toLowerCase().includes(query)
    );
  }, [suppliers, includeArchived, searchQuery]);

  const sourceTypeOptions = useMemo(
    () => deriveSourceTypeOptions(facetSource, facets),
    [facetSource, facets]
  );
  const brandOptions = useMemo(
    () => deriveBrandOptions(facetSource, facets),
    [facetSource, facets]
  );
  const coverageOptions = useMemo(
    () => deriveCoverageOptions(facetSource, facets),
    [facetSource, facets]
  );

  const toolbarSourceTypeOptions = useMemo(
    () =>
      [...new Set(suppliers.map((supplier) => supplier.sourceType))]
        .filter((value): value is string => !!value)
        .sort((a, b) =>
          sourceTypeLabelOf(a).localeCompare(sourceTypeLabelOf(b))
        )
        .map((value) => ({ label: sourceTypeLabelOf(value), value })),
    [suppliers]
  );

  const visibleSuppliers = useMemo(() => {
    const filtered = applyFacets(facetSource, facets);
    const compare = orderBy ? comparatorFor(orderBy) : null;
    return compare
      ? [...filtered].sort((a, b) => {
          const result = compare(a, b);
          return isOrderAscending ? result : -result;
        })
      : [...filtered].sort((a, b) => a.supplierId.localeCompare(b.supplierId));
  }, [facetSource, facets, orderBy, isOrderAscending]);

  const sourceType =
    facets.sourceTypes.size === 1 ? [...facets.sourceTypes][0] : null;

  const handleSortColumn = (column: SupplierSortColumn) => {
    if (orderBy !== column) {
      setOrderBy(column);
      setIsOrderAscending(true);
    } else if (isOrderAscending) {
      setIsOrderAscending(false);
    } else {
      setOrderBy(null);
    }
  };

  const handleChangeSearchQuery = (event: ChangeEvent<HTMLInputElement>) =>
    setSearchQuery(event.target.value);

  const handleClearFilters = () => setFacets(emptySupplierFacetState());

  const buildMenuOptions = (supplier: Supplier): SkuMenuOption[] => [
    {
      label: 'Edit',
      action: () => navigate(`/supplier-master/edit/${supplier.id}`),
    },
  ];

  return (
    <>
      <PageHeader header='Suppliers' pages={pages}>
        <div className='flex items-center gap-2'>
          <Button
            label='Import suppliers'
            intent='primary'
            startIcon={<Squares2X2Icon className='h-5 w-5' />}
            onClick={() => setIsImportModalOpen(true)}
          />
          <Button
            label='Add supplier'
            intent='secondary'
            startIcon={<PlusCircleIcon className='h-5 w-5' />}
            onClick={() => navigate('/supplier-master/add')}
          />
        </div>
      </PageHeader>
      <MasterImportModal
        isOpen={isImportModalOpen}
        setIsOpen={setIsImportModalOpen}
        title='Import suppliers'
        templateHref='/files/supplier_import_template.xlsx'
        templateLabel='Download supplier template'
        templateCaption='One row per supplier — at least one location is required.'
        uploadUrl={SUPPLIER_BULK_CREATE}
        uploadFieldName='suppliersFile'
        dropzoneId='supplier-master-dropzone'
        onUploaded={() => setRefreshCounter((count) => count + 1)}
        aiImportUrl={showAiFeatures ? MASTER_DATA_IMPORT_CREATE : undefined}
        entityType='Supplier'
        onAiImportCreated={(importId) =>
          navigate(`/master-data/imports/${importId}`)
        }
      />
      <PendingImportsBanner entityType='Supplier' refreshKey={refreshCounter} />
      <main className='flex flex-col gap-4 px-8 py-6 font-inter'>
        <SupplierToolbar
          searchQuery={searchQuery}
          onSearchQueryChange={handleChangeSearchQuery}
          sourceType={sourceType}
          onSourceTypeChange={(value) =>
            setFacets((previous) => setSingleSourceType(previous, value))
          }
          sourceTypeOptions={toolbarSourceTypeOptions}
          includeArchived={includeArchived}
          onIncludeArchivedChange={setIncludeArchived}
          isRailVisible={isRailVisible}
          onToggleRail={() => setIsRailVisible((visible) => !visible)}
        />
        <div className='flex items-start gap-6'>
          {isRailVisible && (
            <SupplierFacetRail
              facets={facets}
              sourceTypeOptions={sourceTypeOptions}
              brandOptions={brandOptions}
              coverageOptions={coverageOptions}
              onToggleSourceType={(value) =>
                setFacets(toggleSourceType(facets, value))
              }
              onToggleBrand={(value) => setFacets(toggleBrand(facets, value))}
              onToggleCoverage={(value) =>
                setFacets(toggleCoverage(facets, value))
              }
            />
          )}
          <div className='flex min-w-0 flex-1 flex-col gap-3'>
            <SupplierActiveFilters
              facets={facets}
              onRemoveSourceType={(value) =>
                setFacets(toggleSourceType(facets, value))
              }
              onRemoveBrand={(value) => setFacets(toggleBrand(facets, value))}
              onRemoveCoverage={(value) =>
                setFacets(toggleCoverage(facets, value))
              }
              onClearAll={handleClearFilters}
            />
            <SupplierTable
              suppliers={visibleSuppliers}
              isLoading={isLoading}
              buildMenuOptions={buildMenuOptions}
              hasActiveFilters={hasActiveFacets(facets)}
              onClearFilters={handleClearFilters}
              onAddSupplier={() => navigate('/supplier-master/add')}
              orderBy={orderBy}
              isOrderAscending={isOrderAscending}
              onSortColumn={handleSortColumn}
            />
            {!isLoading && suppliers.length < totalCount && (
              <p className='text-xs text-gray-400'>
                Showing the first {suppliers.length} of {totalCount} suppliers.
                Filters and counts apply to the loaded set — refine your search
                to narrow the list.
              </p>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
